/**
 * HoverCard.jsx — Global mouse-hover summary card for items / users / guilds.
 *
 * Issues #7 + #14: bring back the v1 mouseover popups.
 * Issue #91: keep popup open while cursor is over it; click body to open detail.
 * Issue #89: respect data-icon hint from the source row so the card shows
 *            the exact icon the user is hovering, not a name-resolved fallback.
 *
 * Mechanism: a single mounted-once component attaches a delegated
 * pointerover listener on `document`. When the pointer enters an
 * element matching `.v2-pick--item`, `.v2-pick--user`, or
 * `.v2-pick--guild`, after a short hover-intent delay (HOVER_DELAY_MS)
 * we fetch a small summary and render a positioned popover. Leaving the
 * source element (or the popover) closes it after a brief grace period
 * so users can move into the card to interact with its content.
 *
 * Data sources:
 *   item  → /api/v2/widgets/item-detail?name=…
 *   user  → /api/v2/widgets/user-detail?tag=…
 *   guild → not yet implemented (backlog) — we render a "click to open" hint.
 *
 * Click-through: clicking the card body (or pressing Enter while focused)
 * dispatches the same v2-*-pick event as the source — surfaces the full modal.
 */

import { createEffect, createSignal, Show, For, onCleanup, onMount } from 'solid-js';
import { Portal } from 'solid-js/web';
import api, { describeApiError } from '../lib/apiClient.js';
import { gold, qty, esoIconUrl, ESO_ICON_FALLBACK } from '../lib/format.js';
import { pickItem, pickUser, pickGuild, canInspectUser } from '../lib/picks.js';
import { entityEmoji } from '../lib/emoji.js';
import './HoverCard.css';

const HOVER_DELAY_MS = 350;
const HIDE_GRACE_MS  = 200; // bumped from 120 to bridge the source→card gap reliably (#91)
const CACHE_TTL_MS   = 30_000;
const cache = new Map(); // key → { at, data }

function cacheGet(key) {
  const e = cache.get(key);
  if (!e) return null;
  if (Date.now() - e.at > CACHE_TTL_MS) { cache.delete(key); return null; }
  return e.data;
}
function cacheSet(key, data) { cache.set(key, { at: Date.now(), data }); }

function readPick(el) {
  if (!el || !el.classList) return null;
  const iconHint = el.getAttribute && el.getAttribute('data-icon');
  const explicitKey = el.getAttribute && el.getAttribute('data-pick-key');
  const key = (explicitKey || el.textContent || '').trim();
  if (el.classList.contains('v2-pick--item'))  return { kind: 'item',  key, iconHint: iconHint || null };
  if (el.classList.contains('v2-pick--user'))  return { kind: 'user',  key };
  if (el.classList.contains('v2-pick--guild')) return { kind: 'guild', key };
  return null;
}

function dispatchPickFor(meta) {
  if (!meta || !meta.key) return;
  if (meta.kind === 'item')  pickItem(meta.key, meta.iconHint ? { icon: meta.iconHint } : undefined);
  else if (meta.kind === 'user')  pickUser(meta.key);
  else if (meta.kind === 'guild') pickGuild(meta.key);
}

function emptyHoverMessage(meta) {
  if (meta?.kind === 'item') return 'No item summary is available yet.';
  if (meta?.kind === 'user') return 'No public profile details are available.';
  if (meta?.kind === 'guild') return 'No guild summary is available.';
  return 'No hover details are available.';
}

export default function HoverCard() {
  const [open, setOpen]   = createSignal(false);
  const [pos,  setPos]    = createSignal({ x: 0, y: 0 });
  const [meta, setMeta]   = createSignal(null); // { kind, key }
  const [data, setData]   = createSignal(null);
  const [loading, setLoading] = createSignal(false);
  const [error, setError] = createSignal(null);

  let openTimer = 0;
  let hideTimer = 0;
  let loadSeq = 0;
  let currentEl = null;
  let cardEl    = null;
  let cachedCardW = 320;
  let cachedCardH = 260;
  let lastPointer = { x: 0, y: 0 };
  let placeRaf = 0;
  let resizeObserver = null;

  function clearTimers() {
    if (openTimer) { clearTimeout(openTimer); openTimer = 0; }
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = 0; }
  }

  function placeAt(cx, cy) {
    // Position to the right of the cursor; flip left if near viewport edge.
    // Prefer above the cursor; flip below if near top.
    // Only viewport boundaries are considered — sibling windows are ignored.
    const PAD = 14;
    const cardW = cachedCardW || 320;
    const cardH = cachedCardH || 260;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let x = cx + PAD;
    if (x + cardW > vw - 8) x = cx - cardW - PAD;
    let y = cy - cardH - PAD;
    if (y < 8) y = cy + PAD;
    x = Math.max(8, Math.min(vw - cardW - 8, x));
    y = Math.max(8, Math.min(vh - cardH - 8, y));
    setPos({ x, y });
  }

  function schedulePlace() {
    if (!open()) return;
    if (placeRaf) return;
    placeRaf = requestAnimationFrame(() => {
      placeRaf = 0;
      if (cardEl) {
        cachedCardW = cardEl.offsetWidth || 320;
        cachedCardH = cardEl.offsetHeight || 260;
      }
      placeAt(lastPointer.x, lastPointer.y);
    });
  }

  function setCardRef(el) {
    cardEl = el;
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
    if (el) {
      cachedCardW = el.offsetWidth || 320;
      cachedCardH = el.offsetHeight || 260;
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver((entries) => {
          const entry = entries?.[0];
          if (entry?.borderBoxSize?.[0]) {
            cachedCardW = entry.borderBoxSize[0].inlineSize || 320;
            cachedCardH = entry.borderBoxSize[0].blockSize || 260;
          } else if (cardEl) {
            cachedCardW = cardEl.offsetWidth || 320;
            cachedCardH = cardEl.offsetHeight || 260;
          }
          schedulePlace();
        });
        resizeObserver.observe(el);
      }
    }
    schedulePlace();
  }

  function isCurrentLoad(seq, kind, key) {
    const m = meta();
    return seq === loadSeq && open() && m?.kind === kind && m?.key === key;
  }

  async function loadFor(kind, key) {
    const seq = ++loadSeq;
    const cacheKey = kind + ':' + key.toLowerCase();
    const cached = cacheGet(cacheKey);
    if (cached) {
      if (isCurrentLoad(seq, kind, key)) {
        setData(cached);
        setError(null);
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    setError(null);
    setData(null);
    try {
      let payload = null;
      if (kind === 'user') {
        if (!canInspectUser(key)) { setLoading(false); return; }
        const r = await api.fetchWidget('user-detail', { tag: key });
        payload = r?.payload?.user || null;
      } else if (kind === 'item') {
        const r = await api.fetchWidget('item-detail', { name: key, hover: true });
        // Issue #264 — Fissal lives at payload.fissal (not under .item),
        // so flatten so ItemSummary sees both `rf/ttc/guildBreakdown`
        // and `fissal` on a single object.
        const det = r?.payload?.item || null;
        payload = det ? { ...det, fissal: r?.payload?.fissal || null } : null;
      } else if (kind === 'guild') {
        const r = await api.fetchWidget('guild-detail', { name: key });
        payload = r?.payload || null;
      } else {
        payload = { _stub: true };
      }
      cacheSet(cacheKey, payload);
      if (isCurrentLoad(seq, kind, key)) setData(payload);
    } catch (e) {
      if (isCurrentLoad(seq, kind, key)) setError(describeApiError(e, 'Hover details'));
    } finally {
      if (isCurrentLoad(seq, kind, key)) setLoading(false);
    }
  }

  function show(el, cx, cy) {
    const pick = readPick(el);
    if (!pick || !pick.key) return;
    if (currentEl === el && (open() || openTimer)) return;
    currentEl = el;
    lastPointer = { x: cx, y: cy };
    clearTimers();
    openTimer = setTimeout(() => {
      placeAt(lastPointer.x, lastPointer.y);
      setMeta(pick);
      setOpen(true);
      schedulePlace();
      loadFor(pick.kind, pick.key);
    }, HOVER_DELAY_MS);
  }

  function showFocused(el) {
    const rect = el.getBoundingClientRect();
    show(el, rect.left + rect.width / 2, rect.top + Math.min(rect.height, 36));
  }

  function scheduleHide() {
    clearTimers();
    hideTimer = setTimeout(() => {
      loadSeq += 1;
      setOpen(false);
      currentEl = null;
      setData(null);
      setError(null);
      setLoading(false);
      setMeta(null);
    }, HIDE_GRACE_MS);
  }

  // Delegated handlers ---------------------------------------------------
  function onOver(ev) {
    const t = ev.target;
    if (!t || !t.closest) return;
    if (cardEl && cardEl.contains(t)) {
      // Inside the card — keep it open.
      clearTimers();
      return;
    }
    const pick = t.closest('.v2-pick--item, .v2-pick--user, .v2-pick--guild');
    if (pick) { show(pick, ev.clientX, ev.clientY); return; }
  }
  function onOut(ev) {
    if (!currentEl && !open()) return;
    const t = ev.target;
    const next = ev.relatedTarget;
    if (!t || !t.closest) return;
    if (cardEl && cardEl.contains(t)) {
      // Leaving the card; if not heading back into the source, hide.
      if (!next || (currentEl && !currentEl.contains(next) && (!cardEl.contains(next)))) {
        scheduleHide();
      }
      return;
    }
    const pick = t.closest('.v2-pick--item, .v2-pick--user, .v2-pick--guild');
    if (!pick) return;
    if (next && (pick.contains(next) || (cardEl && cardEl.contains(next)))) return;
    scheduleHide();
  }
  function onMove(ev) {
    if (!currentEl) return;
    const t = ev.target;
    if (!t || !currentEl.contains(t)) return;
    lastPointer = { x: ev.clientX, y: ev.clientY };
    if (open()) schedulePlace();
  }
  function onFocusIn(ev) {
    const t = ev.target;
    if (!t || !t.closest) return;
    const pick = t.closest('.v2-pick--item, .v2-pick--user, .v2-pick--guild');
    if (pick) showFocused(pick);
  }
  function onFocusOut(ev) {
    const t = ev.target;
    const next = ev.relatedTarget;
    if (!t || !t.closest) return;
    const pick = t.closest('.v2-pick--item, .v2-pick--user, .v2-pick--guild');
    if (!pick) return;
    if (next && (pick.contains(next) || (cardEl && cardEl.contains(next)))) return;
    scheduleHide();
  }
  function onScroll() { if (open()) { setOpen(false); clearTimers(); } }
  function onKey(e)   { if (e.key === 'Escape' && open()) { setOpen(false); clearTimers(); } }

  createEffect(() => {
    open();
    meta();
    data();
    loading();
    error();
    schedulePlace();
  });

  onMount(() => {
    document.addEventListener('pointerover', onOver, true);
    document.addEventListener('pointerout',  onOut,  true);
    document.addEventListener('pointermove', onMove, true);
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('keydown', onKey);
  });
  onCleanup(() => {
    clearTimers();
    if (placeRaf) cancelAnimationFrame(placeRaf);
    if (resizeObserver) resizeObserver.disconnect();
    document.removeEventListener('pointerover', onOver, true);
    document.removeEventListener('pointerout',  onOut,  true);
    document.removeEventListener('pointermove', onMove, true);
    document.removeEventListener('focusin', onFocusIn, true);
    document.removeEventListener('focusout', onFocusOut, true);
    window.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('keydown', onKey);
  });

  return (
    <Show when={open()}>
      <Portal>
        <div
          class="v2-hover-card"
          ref={setCardRef}
          style={{ left: pos().x + 'px', top: pos().y + 'px' }}
          tabindex="0"
          role="button"
          aria-label={meta() ? `Open detail for ${meta().key}` : 'Open detail'}
          onMouseEnter={() => clearTimers()}
          onMouseLeave={() => scheduleHide()}
          onClick={(e) => {
            // Don't hijack clicks on inner pickable elements (sub-rows
            // are already wired to their own pick handlers).
            if (e.target && e.target.closest && e.target.closest('.v2-pick')) return;
            const m = meta();
            setOpen(false);
            clearTimers();
            dispatchPickFor(m);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              const m = meta();
              setOpen(false);
              clearTimers();
              dispatchPickFor(m);
            }
          }}
        >
          <Show when={meta()}>
            <header class="v2-hover-card__hdr">
              <span class="v2-hover-card__icon">
                {entityEmoji(meta().kind)}
              </span>
              <span class="v2-hover-card__title">{meta().key}</span>
              {/* Issue #265 — promote time qualifier to the popup heading
                  for user/guild cards so the same scope applies to all
                  three weekly stats (gold/sales/items), not just gold. */}
              <Show when={(meta()?.kind === 'user' || meta()?.kind === 'guild')
                && !loading() && !error() && data()}>
                <span class="v2-hover-card__qualifier" title="Current trade week">(T-WK)</span>
              </Show>
              <span class="v2-hover-card__hint">click to open</span>
            </header>
          </Show>
          <div class="v2-hover-card__body">
            <Show when={loading()}><div class="v2-widget-skel">Loading…</div></Show>
            <Show when={error()}><div class="v2-error">{error()}</div></Show>
            <Show when={!loading() && !error() && meta()?.kind === 'item' && data()}>
              <ItemSummary item={data()} iconHint={meta()?.iconHint} />
            </Show>
            <Show when={!loading() && !error() && meta()?.kind === 'user' && data()}>
              <UserSummary user={data()} />
            </Show>
            <Show when={!loading() && !error() && meta()?.kind === 'guild' && data()}>
              <Show when={data()?.found} fallback={
                <div class="v2-hover-card__stub">Guild not tracked.</div>
              }>
                <GuildSummary detail={data()} />
              </Show>
            </Show>
            <Show when={!loading() && !error() && meta() && !data()}>
              <div class="v2-hover-card__stub">{emptyHoverMessage(meta())}</div>
            </Show>
          </div>
        </div>
      </Portal>
    </Show>
  );
}

function ItemSummary(props) {
  const it = props.item || {};
  const rf = it.rf || it.RF || it; // tolerate either shape
  const ttc = it.ttc || null; // Issue #27 — TTC alongside MM avg
  const fissal = props.item?.fissal || null;
  // #89: prefer the icon the user was hovering (passed via data-icon)
  // over the name-resolved icon returned by the endpoint.
  const icon  = props.iconHint || rf.icon || it.icon;
  const traits = Array.isArray(rf.traits) ? rf.traits : [];
  const ttcSuggest = ttc != null ? (ttc.suggestedPrice ?? ttc.avgPrice) : null;
  const ttcHasBand = ttc != null
    && ttc.minPrice != null && ttc.maxPrice != null
    && ttc.maxPrice > ttc.minPrice;
  // Issue #264 — top guilds for this item (limit 3, by gold).
  const topGuilds = Array.isArray(it.guildBreakdown)
    ? [...it.guildBreakdown]
        .sort((a, b) => (b.avgPrice * b.volumeCount) - (a.avgPrice * a.volumeCount))
        .slice(0, 3)
    : [];
  const fissalPrice  = fissal?.fissalSuggested ?? fissal?.suggestedPrice ?? null;
  const fissalConf   = typeof fissal?.confidence === 'number'
    ? Math.round(fissal.confidence * 100) + '%'
    : (fissal?.confidence || null);
  const fissalReason = fissal?.reasoning?.text || null;
  return (
    <>
      <div class="v2-hover-card__row">
        <Show when={icon}>
          <img class="v2-item-icon v2-item-icon--sm" src={esoIconUrl(icon)} alt=""
            onError={(e) => { e.currentTarget.src = ESO_ICON_FALLBACK; }} />
        </Show>
        <div class="v2-hover-card__col">
          {/* Issue #27 — compact MM + TTC side-by-side */}
          <div class="v2-hover-card__price-row">
            <Show when={rf.avgPrice != null}>
              <span class="v2-hover-card__price-block">
                <span class="v2-hover-card__price-tag v2-hover-card__price-tag--mm">MM</span>
                {gold(rf.avgPrice)}
              </span>
            </Show>
            <Show when={ttcSuggest != null && ttcSuggest > 0}>
              <span class="v2-hover-card__price-block">
                <span class="v2-hover-card__price-tag v2-hover-card__price-tag--ttc">TTC</span>
                {gold(ttcSuggest)}
              </span>
            </Show>
          </div>
          <Show when={ttcHasBand}>
            <div class="v2-rank-row__sub">
              TTC range {gold(ttc.minPrice)} – {gold(ttc.maxPrice)}
              <Show when={ttc.variantCount > 1}> · {qty(ttc.variantCount)} variants</Show>
            </div>
          </Show>
          <Show when={rf.totalSales != null || rf.saleCount != null}>
            <div class="v2-rank-row__sub">
              {qty(rf.totalSales ?? rf.saleCount ?? 0)} sales
              <Show when={(rf.totalItems ?? rf.volumeCount) != null}>
                {' '}· {qty(rf.totalItems ?? rf.volumeCount)} items
              </Show>
            </div>
          </Show>
        </div>
      </div>
      <Show when={fissalPrice != null && fissalPrice > 0}>
        <div class="v2-hover-card__row">
          <span class="v2-hover-card__pill">Fissal</span>
          <div class="v2-hover-card__col">
            <div><b>Suggested:</b> {gold(fissalPrice)}
              <Show when={fissalConf}>
                <span class="v2-rank-row__sub" style={{ 'margin-left': '6px' }}>
                  ({fissalConf})
                </span>
              </Show>
            </div>
            <Show when={fissalReason}>
              <div class="v2-rank-row__sub v2-hover-card__fissal-reason">
                {fissalReason}
              </div>
            </Show>
          </div>
        </div>
      </Show>
      <Show when={topGuilds.length}>
        <div class="v2-hover-card__breakdown">
          <div class="v2-hover-card__sub">Top guilds</div>
          <For each={topGuilds}>{(g) => (
            <div class="v2-hover-card__line">
              <span class="v2-hover-card__truncate">{g.guildName}</span>
              <span>{gold(g.avgPrice)} · {qty(g.saleCount)}</span>
            </div>
          )}</For>
        </div>
      </Show>
      <Show when={traits.length}>
        <div class="v2-hover-card__breakdown">
          <div class="v2-hover-card__sub">By trait</div>
          <For each={traits.slice(0, 4)}>{(t) => (
            <div class="v2-hover-card__line">
              <span>{t.trait || t.name || '—'}</span>
              <span>{gold(t.avgPrice ?? t.avg ?? 0)}</span>
            </div>
          )}</For>
        </div>
      </Show>
    </>
  );
}

function UserSummary(props) {
  const u = props.user || {};
  const lt = u.lifetime || null;
  if (!u.found) {
    return (
      <div class="v2-empty">
        {u.hidden?.saleHistory ? 'Sale history is private.' : 'No sales tracked.'}
        {lt && lt.totalSales > 0 ? (
          <div class="v2-rank-row__sub" style={{ 'margin-top': '4px' }}>
            (lifetime: {qty(lt.totalSales)} sales · {gold(lt.totalGold)})
          </div>
        ) : null}
      </div>
    );
  }
  const hasWeek = (u.totalSales || 0) > 0;
  // Issue #265 — admin-only dues pills per Redfur home guild.
  // Backend only populates `duesByGuild` when ctx.identity.isAdmin, so
  // its presence is itself the gate (no client-side viewer check needed).
  const duesEntries = u.duesByGuild
    ? Object.entries(u.duesByGuild)
    : [];
  return (
    <>
      <Show when={hasWeek}>
        <div class="v2-hover-card__stat-grid">
          <div class="v2-stat v2-stat--gold">
            <span class="v2-stat__label">Gold</span>
            <span class="v2-stat__value">{gold(u.totalGold)}</span>
          </div>
          <div class="v2-stat v2-stat--cyan">
            <span class="v2-stat__label">Sales</span>
            <span class="v2-stat__value">{qty(u.totalSales)}</span>
          </div>
          <div class="v2-stat">
            <span class="v2-stat__label">Items</span>
            <span class="v2-stat__value">{qty(u.totalItems)}</span>
          </div>
        </div>
      </Show>
      <Show when={u.hidden?.saleHistory}>
        <div class="v2-hover-card__stub">Sale history and raffle activity are private.</div>
      </Show>
      <Show when={!u.hidden?.saleHistory && (u.performance || u.raffle)}>
        <div class="v2-hover-card__breakdown">
          <div class="v2-hover-card__sub">Profile</div>
          <Show when={u.performance}>
            <div class="v2-hover-card__line">
              <span>Performance</span>
              <span>{u.performance.score}/100 · {u.performance.label}</span>
            </div>
          </Show>
          <Show when={u.raffle}>
            <div class="v2-hover-card__line">
              <span>Raffle</span>
              <span>{qty(u.raffle.totalTickets)} tickets</span>
            </div>
          </Show>
        </div>
      </Show>
      <Show when={duesEntries.length}>
        <div class="v2-hover-card__dues-row" aria-label="Dues status by guild (admin only)">
          <For each={duesEntries}>{([key, d]) => (
            <span
              class={`v2-dues-pill v2-dues-pill--${d.duesMet ? 'met' : 'short'}`}
              title={`${d.guildName || key.toUpperCase()} · ${d.windowDays}d window` +
                     (d.minSales ? ` · need ${d.minSales} sale${d.minSales > 1 ? 's' : ''}` : '') +
                     (d.minGold  ? ` · need ${qty(d.minGold)}g`                              : '') +
                     ` · in window: ${qty(d.salesInWindow)} sales / ${qty(d.goldInWindow)}g`}
            >
              <span class="v2-dues-pill__name">{key.toUpperCase()}</span>
              <Show when={d.minSales > 0}>
                <span class={`v2-dues-pill__flag v2-dues-pill__flag--${d.salesMet ? 'met' : 'short'}`}
                      aria-label={`Sales ${d.salesMet ? 'met' : 'short'}`}>S</span>
              </Show>
              <Show when={d.minGold > 0}>
                <span class={`v2-dues-pill__flag v2-dues-pill__flag--${d.goldMet ? 'met' : 'short'}`}
                      aria-label={`Gold ${d.goldMet ? 'met' : 'short'}`}>G</span>
              </Show>
            </span>
          )}</For>
        </div>
      </Show>
      <Show when={lt && lt.totalSales > 0}>
        <div class="v2-rank-row__sub" style={{ 'margin-top': hasWeek ? '6px' : '0' }}>
          {lt.source === 'ttc_esohub' ? 'TTC + ESO-Hub:' : 'Lifetime:'} {qty(lt.totalSales)} sales · {gold(lt.totalGold)}
        </div>
      </Show>
      <Show when={!hasWeek && u.ttcIntel?.buyerIntel?.totalPurchases > 0}>
        <div class="v2-rank-row__sub" style={{ 'margin-top': '4px' }}>
          Kiosk Buys: {qty(u.ttcIntel.buyerIntel.totalPurchases)} · {gold(u.ttcIntel.buyerIntel.totalSpentGold)}
          <Show when={u.ttcIntel.buyerIntel.avgBuyVsEsoHubSales != null}>
            {' '}({u.ttcIntel.buyerIntel.avgBuyVsEsoHubSales}x ESO-Hub)
          </Show>
        </div>
      </Show>
      <Show when={u.topItems?.length}>
        <div class="v2-hover-card__breakdown">
          <div class="v2-hover-card__sub">Top items</div>
          <For each={u.topItems.slice(0, 4)}>{(it) => (
            <div class="v2-hover-card__line">
              <span class="v2-hover-card__truncate">
                <Show when={it.itemIcon}>
                  <img class="v2-item-icon v2-item-icon--xs" src={esoIconUrl(it.itemIcon)} alt=""
                    onError={(e) => { e.currentTarget.src = ESO_ICON_FALLBACK; }} />
                </Show>
                {it.itemName}
              </span>
              <span>{gold(it.totalGold)}</span>
            </div>
          )}</For>
        </div>
      </Show>
    </>
  );
}

function GuildSummary(props) {
  const d = props.detail || {};
  const cw = d.currentWeek || {};
  const lt = d.lifetime || {};
  return (
    <>
      <div class="v2-hover-card__stat-grid">
        <div class="v2-stat v2-stat--gold">
          <span class="v2-stat__label">Gold</span>
          <span class="v2-stat__value">{gold(cw.totalGold || 0)}</span>
        </div>
        <div class="v2-stat v2-stat--cyan">
          <span class="v2-stat__label">Sales</span>
          <span class="v2-stat__value">{qty(cw.totalSales || 0)}</span>
        </div>
        <div class="v2-stat">
          <span class="v2-stat__label">Sellers</span>
          <span class="v2-stat__value">{qty(cw.sellerCount || 0)}</span>
        </div>
      </div>
      <Show when={(lt.totalSales || 0) > 0}>
        <div class="v2-rank-row__sub" style={{ 'margin-top': '6px' }}>
          Lifetime: {qty(lt.totalSales)} sales · {gold(lt.totalGold)}
        </div>
      </Show>
      <Show when={d.topSellers?.length}>
        <div class="v2-hover-card__breakdown">
          <div class="v2-hover-card__sub">Top sellers · this week</div>
          <For each={d.topSellers.slice(0, 4)}>{(s) => (
            <div class="v2-hover-card__line">
              <span class="v2-hover-card__truncate">{s.sellerName}</span>
              <span>{gold(s.totalGold)}</span>
            </div>
          )}</For>
        </div>
      </Show>
    </>
  );
}
